import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, type ReactNode } from 'react'
import {
  SEND_CHANNEL_VERB,
  describeSendTracking,
  documentPdfState,
  documentSendState,
  jobContactDuties,
  sendRecipient,
  type DocumentSendLike,
  type DocumentSendState,
} from '@fieldops/shared'
import { cn } from '../../lib/cn.js'
import { fileUrl } from '../../lib/files.js'
import { formatDate, money } from '../../lib/format.js'
import { stageOf } from '../../lib/job-stages.js'
import { useIsNarrow } from '../../lib/media.js'
import { MarkSentPopup } from './MarkSentPopup.js'
import { RaiseDocumentPopup } from './RaiseDocumentPopup.js'
import { CatalogLineField } from './CatalogLineField.js'
import { CatalogItemPopup, type CatalogItem } from '../catalog/CatalogItemPopup.js'
import { ProposalBuilder } from '../proposal/ProposalBuilder.js'
import {
  AcceptancePanel,
  DeclinedNote,
  OfficeAcceptPopup,
  ProposalLines,
  ViewedNote,
  acceptNeedsChoice,
  isProposalQuote,
  proposalRailTotals,
} from './ProposalPanels.js'
import { SendDocumentPopup, type SendAddressee } from './SendDocumentPopup.js'
import { useSession } from '../../lib/session.js'
import { errorMessage, useTRPC, type RouterOutputs } from '../../lib/trpc.js'
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  INVOICE_STATUS_TONE,
  QUOTE_STATUS_TONE,
  SkeletonRows,
  buttonClass,
  humanise,
} from '../ui.js'
import { Icon } from '../Icon.js'
import { Label, Panel, PanelHeading } from './panes.js'
import { PaymentPopup } from './PaymentPopup.js'
import type { Job } from './types.js'

/** Who a quote or invoice offers to send to, with the billing contact selected. */
function sendAddressee(job: Job): SendAddressee {
  const billing = jobContactDuties(job.contacts).billing

  return {
    recipients: job.contacts.flatMap((link) => {
      const email = link.contact.email
      if (!email) return []
      const name = [link.contact.firstName, link.contact.lastName].filter(Boolean).join(' ')
      return [{ name, email }]
    }),
    fallbackTo: billing?.contact.email ?? job.client.email,
    jobId: job.id,
    files: job.attachments.map((file) => ({ id: file.id, name: file.filename })),
  }
}

/**
 * What was quoted and what was billed.
 *
 * `jobs.byId` returns quote and invoice headers but no lines, so the tables
 * come from `quotes.byId` and `invoices.byId` — fetched only once this tab is
 * open, because most people opening a job from the board never look at it and
 * two extra round trips on every booking they glance at is a poor trade.
 *
 * Only the latest of each is shown. A job with three superseded quotes on it
 * is a job where one of them is the live number and the rest are history, and
 * history belongs in the activity column, which already lists them.
 *
 * Lines are edited here: description, quantity, unit price, add and remove.
 * The Item box searches our catalog (`CatalogLineField`) and can add to it;
 * an item's code is drawn under the line and never printed. A paid invoice
 * stays read-only — a credit note is the instrument once money has been
 * received.
 *
 * On a desk the quote mockup wins: lines in the details column, totals and
 * the next action in a billing rail where the feed sat. The activity column
 * is only on Work (decision 52, amended), so this tab does not keep a note
 * composer beside a price. On a phone there is no rail — totals stay in the
 * foot of the document, the way they always did, because a second column
 * under 1023px is the sheet tree and not this one.
 *
 * Each document is one card: the number and its state along the top, the
 * lines through the middle. The mockup's line is a name and an amount; the
 * quantity, the unit price and whether the line carried tax stay, because on
 * a real invoice they are the three things somebody rings up to argue about.
 */
export function BillingPane({ job }: { job: Job }) {
  const { can } = useSession()
  const split = !useIsNarrow()

  const quote = job.quotes.find((row) => row.status !== 'SUPERSEDED') ?? job.quotes[0]
  const invoice = job.invoices.find((row) => row.status !== 'VOID') ?? job.invoices[0]
  // Moving the stage back to Quote does not remove an invoice, and the empty
  // state — the only place "Write a proposal" used to live — never renders
  // once one exists. A quote already on the job keeps "Make it a proposal".
  const offerProposal = stageOf(job.status) === 'QUOTE' && !quote && can('QUOTE_EDIT')

  if (!quote && !invoice) {
    return (
      <BillingShell job={job}>
        <Panel>
          <NothingBilled job={job} />
        </Panel>
      </BillingShell>
    )
  }

  const showQuote = Boolean(quote && can('QUOTE_VIEW'))
  const showInvoice = Boolean(invoice && can('INVOICE_VIEW'))

  // A tab that renders nothing at all reads as broken. Most people on this
  // system are field staff whose role does not include pricing, and they are
  // owed a sentence rather than an empty panel.
  if (!showQuote && !showInvoice) {
    return (
      <BillingShell job={job}>
        <Panel>
          <EmptyState
            title="Not your area"
            description="This job has been quoted or invoiced, but your role does not include seeing prices."
          />
        </Panel>
      </BillingShell>
    )
  }

  // The invoice is the live money when both exist. The quote stays on the
  // left as history and keeps its own foot totals, because the rail is the
  // invoice's and a second total beside it would be two answers.
  //
  // Unless the job is in Quote and has never been quoted. Then the rail leads
  // with the proposal, and the invoice's own action steps back so the green
  // button is the one the stage is asking for. On a phone there is no rail,
  // so the same control sits above the invoice.
  return (
    <BillingShell
      job={job}
      rail={
        split ? (
          showInvoice && invoice ? (
            <InvoiceRail
              invoiceId={invoice.id}
              addressee={sendAddressee(job)}
              proposal={offerProposal ? <WriteProposal job={job} placement="rail" /> : null}
            />
          ) : quote ? (
            <QuoteRail quoteId={quote.id} addressee={sendAddressee(job)} />
          ) : null
        ) : null
      }
    >
      {offerProposal && !split ? <WriteProposal job={job} placement="column" /> : null}
      {showQuote && quote ? (
        <QuoteBlock
          quoteId={quote.id}
          railTakesTotals={split && !showInvoice}
          quoting={stageOf(job.status) === 'QUOTE'}
          addressee={sendAddressee(job)}
        />
      ) : null}
      {showInvoice && invoice ? (
        <InvoiceBlock
          invoiceId={invoice.id}
          railTakesTotals={split}
          yieldToProposal={offerProposal && !split}
          addressee={sendAddressee(job)}
        />
      ) : null}
    </BillingShell>
  )
}

/**
 * The two columns of the billing tab on a desk; a stack on a phone.
 *
 * JobCard hands this pane the whole body when the tab is open, so the rail
 * sits where the activity column sat rather than nesting inside Job details.
 * The work done leads the column in every state of the tab, because it is
 * what the invoice is written from.
 */
function BillingShell({
  job,
  children,
  rail,
}: {
  job: Job
  children: ReactNode
  rail?: ReactNode
}) {
  const narrow = useIsNarrow()
  const body = (
    <>
      <WorkDone text={job.workDone} />
      {children}
    </>
  )

  if (narrow) return body

  return (
    <>
      <section
        aria-label="Job details"
        className={cn(
          'flex min-w-0 flex-1 flex-col px-5 py-1 lg:overflow-y-auto',
          rail ? 'lg:border-r lg:border-ink-200' : null,
        )}
      >
        {body}
      </section>
      {rail}
    </>
  )
}

/** What the technician recorded doing, as ServiceM8 imported it. Read only. */
function WorkDone({ text }: { text: string | null }) {
  if (!text) return null

  return (
    <Panel>
      <Label>Work done</Label>
      <p className="text-sm leading-relaxed whitespace-pre-wrap text-ink-700">{text}</p>
    </Panel>
  )
}

/**
 * The first document on a job, which until now could not be raised at all.
 *
 * `quotes.create` and `invoices.create` have existed since the schema did and
 * had no caller anywhere in the client: every quote in the system arrived from
 * ServiceM8, a seed or a test. So this is the whole entry point.
 *
 * **The job's stage decides which one leads.** A job in Quote is being priced
 * and wants a quote; a Work Order or a finished job is being billed and wants
 * an invoice. The other stays available as a quieter action rather than hidden,
 * because a job that walked in as a work order still sometimes needs a price in
 * writing, and hiding it would leave the office with no screen to do that on.
 */
function NothingBilled({ job }: { job: Job }) {
  const { can } = useSession()
  // A quote opens the proposal builder, which offers the plain price list as
  // its "quick quote"; that one is still `RaiseDocumentPopup`.
  const [raising, setRaising] = useState<'quote' | 'quick-quote' | 'invoice' | null>(null)

  // LEAD, QUOTING and QUOTED are the Quote stage. Everything else is work
  // that has been sold, so the money owed is the live question.
  const quoting = stageOf(job.status) === 'QUOTE'

  const mayQuote = can('QUOTE_EDIT')
  const mayInvoice = can('INVOICE_EDIT')

  if (!mayQuote && !mayInvoice) {
    return (
      <EmptyState
        title="Nothing billed yet"
        description="No quote and no invoice on this job, and pricing is not part of your role."
      />
    )
  }

  const lead: 'quote' | 'invoice' = quoting && mayQuote ? 'quote' : mayInvoice ? 'invoice' : 'quote'
  const other = lead === 'quote' ? 'invoice' : 'quote'
  const otherAllowed = other === 'quote' ? mayQuote : mayInvoice

  return (
    <>
      <EmptyState
        title="Nothing billed yet"
        description={
          lead === 'quote'
            ? 'This job is being quoted. Put the price in writing and send it.'
            : 'The work is sold. Bill it.'
        }
        action={
          <div className="flex flex-wrap items-center justify-center gap-2">
            <Button variant="primary" onClick={() => setRaising(lead)}>
              {lead === 'quote' ? 'Write a proposal' : 'Raise an invoice'}
            </Button>
            {otherAllowed ? (
              <Button variant="ghost" onClick={() => setRaising(other)}>
                {other === 'quote' ? 'Quote it instead' : 'Invoice it instead'}
              </Button>
            ) : null}
          </div>
        }
      />

      <RaiseDocumentPopup
        kind={raising === 'quick-quote' ? 'quote' : 'invoice'}
        job={job}
        open={raising === 'invoice' || raising === 'quick-quote'}
        onClose={() => setRaising(null)}
      />
      <ProposalBuilder
        kind="quote"
        job={job}
        open={raising === 'quote'}
        onClose={() => setRaising(null)}
        onQuickQuote={() => setRaising('quick-quote')}
      />
    </>
  )
}

/**
 * Starting a proposal on a job that already has an invoice.
 *
 * The empty billing tab is the other door. This one is for a job moved back
 * to Quote after a draft invoice was raised: the invoice stays, and this is
 * the action the stage is now asking for.
 */
function WriteProposal({ job, placement }: { job: Job; placement: 'rail' | 'column' }) {
  const [raising, setRaising] = useState<'quote' | 'quick-quote' | null>(null)
  const copy = 'This job is being quoted. Put the price in writing and send it.'

  return (
    <>
      {placement === 'rail' ? (
        <div className="flex flex-col gap-2">
          <p className="text-[13px] leading-relaxed text-ink-600">{copy}</p>
          <Button variant="primary" block onClick={() => setRaising('quote')}>
            Write a proposal
          </Button>
        </div>
      ) : (
        <Panel>
          <p className="text-[13px] leading-relaxed text-ink-700">{copy}</p>
          <div className="mt-3">
            <Button variant="primary" onClick={() => setRaising('quote')}>
              Write a proposal
            </Button>
          </div>
        </Panel>
      )}
      <RaiseDocumentPopup
        kind="quote"
        job={job}
        open={raising === 'quick-quote'}
        onClose={() => setRaising(null)}
      />
      <ProposalBuilder
        kind="quote"
        job={job}
        open={raising === 'quote'}
        onClose={() => setRaising(null)}
        onQuickQuote={() => setRaising('quick-quote')}
      />
    </>
  )
}

/**
 * Whether the customer is holding what is on the screen.
 *
 * The one sentence this whole feature exists to be able to write. `sentAt`
 * could only ever say *that* something was sent; the send rows say which
 * version, so an invoice edited after it went out stops claiming to be sent.
 */
function SendState({
  state,
  kind,
}: {
  state: DocumentSendState
  kind: 'quote' | 'invoice'
}) {
  if (state.status === 'never') return null

  if (state.status === 'unknown') {
    return (
      <p className="text-xs text-ink-500">
        Sent before this version tracking existed, so which copy the customer
        has is not recorded.
      </p>
    )
  }

  const latest = state.latest!

  if (state.status === 'changed') {
    return (
      <div
        role="status"
        className="flex flex-col gap-1 rounded-md border border-amber-300 bg-amber-50 px-3 py-2"
      >
        <p className="text-[12.5px] font-semibold text-amber-900">
          Changed since it was sent
        </p>
        <p className="text-xs text-amber-800">
          The customer has v{latest.version}, {SEND_CHANNEL_VERB[latest.channel]}{' '}
          {formatDate(latest.sentAt)}. This {kind} has been edited since — send it again so
          they have the current figures.
        </p>
      </div>
    )
  }

  return (
    <p className="text-xs text-ink-500">
      The customer has this version — v{latest.version},{' '}
      {SEND_CHANNEL_VERB[latest.channel]} {formatDate(latest.sentAt)}
      {latest.sentBy ? ` by ${latest.sentBy.name}` : ''}.
    </p>
  )
}

/**
 * What the customer did with the newest copy of an invoice, beside its totals.
 *
 * A quote says this with `ViewedNote`, from its links. An invoice's link lives
 * on its send, so this reads the newest one. Decision 88.
 */
function LatestSendTracking({ sends }: { sends: DocumentSendLike[] }) {
  const latest = [...sends].sort((a, b) => b.version - a.version)[0]
  const tracking = latest ? describeSendTracking(latest, formatDate) : null
  if (!tracking) return null

  return (
    <p className={cn('text-xs', tracking.problem ? 'font-medium text-red-700' : 'text-ink-600')}>
      {tracking.text}
    </p>
  )
}

/**
 * When each copy went, to whom, by whom, and the exact PDF that went.
 *
 * The version number is the load-bearing part: "sent again" without one does
 * not tell anybody which figures the customer is looking at.
 */
function SendHistory({ sends }: { sends: DocumentSendLike[] }) {
  if (sends.length === 0) return null

  return (
    <Panel>
      <PanelHeading
        title="Sent to the customer"
        hint={sends.length === 1 ? 'Once' : `${sends.length} times`}
      />
      <ul className="flex flex-col divide-y divide-ink-100">
        {sends.map((send) => {
          const url = send.attachment ? fileUrl(send.attachment) : null
          const to = sendRecipient(send)
          const tracking = describeSendTracking(send, formatDate)

          return (
            <li key={`${send.version}`} className="flex flex-col gap-0.5 py-2 text-[13px]">
              <span className="flex flex-wrap items-baseline gap-x-2">
                <span className="font-mono text-xs font-semibold text-ink-600">
                  v{send.version}
                </span>
                <span className="text-ink-800">
                  {SEND_CHANNEL_VERB[send.channel]}
                  {to ? ` to ${to}` : ''}
                </span>
                <span className="ml-auto shrink-0 tabular-nums text-xs text-ink-500">
                  {formatDate(send.sentAt)}
                </span>
              </span>
              <span className="flex flex-wrap items-baseline gap-x-2 text-xs text-ink-500">
                {send.sentBy ? <span>by {send.sentBy.name}</span> : null}
                {url ? (
                  <a
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-ink-700 underline decoration-ink-300 hover:text-ink-900"
                  >
                    The copy they got
                  </a>
                ) : null}
              </span>
              {tracking ? (
                <span
                  className={cn(
                    'text-xs',
                    tracking.problem ? 'font-medium text-red-700' : 'text-ink-600',
                  )}
                >
                  {tracking.text}
                </span>
              ) : null}
            </li>
          )
        })}
      </ul>
    </Panel>
  )
}

/**
 * The stored PDF: save one, open it, or draw it again when it is out of date.
 *
 * Opening is an anchor wearing the button skin, not a `Button` with an
 * onClick: it has to middle-click into a new tab, which is what the office
 * does when it wants the figures beside the screen while it rings somebody.
 *
 * Whether the PDF is current is `documentPdfState` — the hash the render was
 * drawn from against the document's own — so an invoice edited after it was
 * issued offers **Update the PDF** rather than opening yesterday's figures as
 * if they were today's. Saving sends nothing. Decision 88.
 */
function DocumentPdf({
  kind,
  id,
  document,
  documentHash,
  contentHash,
  size,
  block,
}: {
  kind: 'quote' | 'invoice'
  id: string
  document: { id: string; checksum: string | null; filename: string } | null | undefined
  documentHash: string | null | undefined
  contentHash: string | null | undefined
  size: 'sm' | 'md'
  block?: boolean
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { can } = useSession()

  const onSaved = (result: { attachmentId: string }) => {
    void queryClient.invalidateQueries(
      kind === 'quote' ? trpc.quotes.pathFilter() : trpc.invoices.pathFilter(),
    )
    void queryClient.invalidateQueries(trpc.jobs.pathFilter())
    const url = fileUrl({ id: result.attachmentId, checksum: 'stored' })
    if (url) window.open(url, '_blank', 'noreferrer')
  }

  const saveQuote = useMutation(trpc.quotes.document.mutationOptions({ onSuccess: onSaved }))
  const saveInvoice = useMutation(trpc.invoices.document.mutationOptions({ onSuccess: onSaved }))
  const save = kind === 'quote' ? saveQuote : saveInvoice

  const state = documentPdfState({ document, documentHash, contentHash })
  const url = document ? fileUrl(document) : null
  const mayView = can(kind === 'quote' ? 'QUOTE_VIEW' : 'INVOICE_VIEW')

  return (
    <>
      {url ? (
        <a href={url} target="_blank" rel="noreferrer" className={buttonClass({ size, block })}>
          View the PDF
        </a>
      ) : null}
      {mayView && (state !== 'current' || !url) ? (
        <Button
          size={size}
          variant={url ? 'ghost' : 'secondary'}
          block={block}
          loading={save.isPending}
          onClick={() => save.mutate({ id })}
        >
          {url ? 'Update the PDF' : 'Save PDF'}
        </Button>
      ) : null}
      {save.error ? <p className="text-xs text-red-700">{errorMessage(save.error)}</p> : null}
    </>
  )
}

function QuoteBlock({
  quoteId,
  railTakesTotals,
  quoting,
  addressee,
}: {
  quoteId: string
  railTakesTotals: boolean
  /** The job is in the Quote stage, where a plain quote can always become a proposal. */
  quoting: boolean
  addressee: SendAddressee
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { can } = useSession()
  const [sending, setSending] = useState(false)
  const [marking, setMarking] = useState(false)
  // Which quote the builder is open on. Not a boolean: making a sent quote a
  // proposal opens the builder on the new version, not on this one.
  const [building, setBuilding] = useState<string | null>(null)

  const { data: quote, isPending, error, refetch } = useQuery(trpc.quotes.byId.queryOptions({ id: quoteId }))

  const invalidate = () => {
    void queryClient.invalidateQueries(trpc.quotes.pathFilter())
    void queryClient.invalidateQueries(trpc.jobs.pathFilter())
  }

  const revise = useMutation(trpc.quotes.revise.mutationOptions({ onSuccess: invalidate }))
  const respond = useMutation(trpc.quotes.respond.mutationOptions({ onSuccess: invalidate }))
  const updateLines = useMutation(trpc.quotes.update.mutationOptions({ onSuccess: invalidate }))

  if (error) return <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
  if (isPending) return <SkeletonRows rows={3} />

  const proposal = isProposalQuote(quote)
  const sendState = documentSendState(quote)
  const totals = proposal || acceptNeedsChoice(quote)
    ? proposalRailTotals(quote)
    : { rows: quoteTotals(quote, partsOf(quote.lines, quote)), total: ['Total', quote.totalIncTax] as [string, number] }
  const drafting = can('QUOTE_EDIT') && quote.status === 'DRAFT'
  // A sent or declined price list becomes a proposal as its next version: the
  // one the customer holds cannot change under them (decision 79).
  const convertible =
    !proposal &&
    !drafting &&
    quoting &&
    can('QUOTE_EDIT') &&
    quote.status !== 'ACCEPTED' &&
    quote.status !== 'SUPERSEDED'

  const makeProposal = () => {
    revise.mutate(
      { id: quote.id },
      { onSuccess: (revision) => setBuilding(revision.id) },
    )
  }

  return (
    <>
      <Panel padded={false}>
        <DocumentHeader
          label={proposal ? 'Proposal' : 'Quote'}
          number={quote.number}
          title={quote.title}
          tone={QUOTE_STATUS_TONE[quote.status] ?? 'neutral'}
          status={humanise(quote.status)}
          meta={
            quote.sentAt
              ? `Sent ${formatDate(quote.sentAt)}${quote.validUntil ? ` · valid to ${formatDate(quote.validUntil)}` : ''}`
              : 'Not sent yet'
          }
        >
          {drafting ? (
            <Button size="sm" variant={proposal ? 'primary' : 'secondary'} onClick={() => setBuilding(quote.id)}>
              {proposal ? 'Edit proposal' : 'Make it a proposal'}
            </Button>
          ) : convertible ? (
            <Button size="sm" variant="secondary" loading={revise.isPending} onClick={makeProposal}>
              Make it a proposal
            </Button>
          ) : null}
          {railTakesTotals ? null : (
            <QuoteActions
              quote={quote}
              onSend={() => setSending(true)}
              onMarkSent={() => setMarking(true)}
              revise={revise}
              respond={respond}
              sendState={sendState}
            />
          )}
        </DocumentHeader>

        {proposal ? (
          <ProposalLines quote={quote} />
        ) : (
          <LineTable
            lines={quote.lines}
            clientId={quote.job.clientId}
            editable={drafting}
            onSave={(next) => updateLines.mutate({ id: quote.id, lines: toLineInput(next) })}
            saving={updateLines.isPending}
          />
        )}

        {railTakesTotals ? null : <Totals rows={totals.rows} total={totals.total} />}

        {railTakesTotals ? null : (
          <div className="flex flex-col gap-1 border-t border-ink-100 px-4 py-3">
            <SendState state={sendState} kind="quote" />
            <ViewedNote quote={quote} />
          </div>
        )}
      </Panel>

      <AcceptancePanel quote={quote} />
      <DeclinedNote quote={quote} />

      {building ? (
        <ProposalBuilder
          kind="quote"
          open
          job={{
            id: quote.job.id,
            clientId: quote.job.clientId,
            categoryId: quote.job.categoryId,
            client: { name: quote.job.client.name },
          }}
          quoteId={building}
          onClose={() => setBuilding(null)}
        />
      ) : null}
      {revise.error ? <ErrorState message={errorMessage(revise.error)} /> : null}

      {quote.termsText && !proposal ? (
        <Panel>
          <PanelHeading title="Note on the quote" />
          <p className="text-[13px] leading-relaxed whitespace-pre-wrap text-ink-700">
            {quote.termsText}
          </p>
        </Panel>
      ) : null}

      <SendHistory sends={quote.sends} />

      {railTakesTotals ? null : (
        <>
          <SendDocumentPopup
            kind="quote"
            id={quote.id}
            number={quote.number}
            addressee={addressee}
            open={sending}
            onClose={() => setSending(false)}
          />
          <MarkSentPopup
            kind="quote"
            id={quote.id}
            number={quote.number}
            open={marking}
            onClose={() => setMarking(false)}
          />
        </>
      )}
    </>
  )
}

function QuoteRail({ quoteId, addressee }: { quoteId: string; addressee: SendAddressee }) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const [sending, setSending] = useState(false)
  const [marking, setMarking] = useState(false)

  const { data: quote, isPending, error, refetch } = useQuery(trpc.quotes.byId.queryOptions({ id: quoteId }))

  const invalidate = () => {
    void queryClient.invalidateQueries(trpc.quotes.pathFilter())
    void queryClient.invalidateQueries(trpc.jobs.pathFilter())
  }

  const revise = useMutation(trpc.quotes.revise.mutationOptions({ onSuccess: invalidate }))
  const respond = useMutation(trpc.quotes.respond.mutationOptions({ onSuccess: invalidate }))

  if (error) {
    return (
      <BillingRail>
        <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
      </BillingRail>
    )
  }
  if (isPending || !quote) {
    return (
      <BillingRail>
        <SkeletonRows rows={3} />
      </BillingRail>
    )
  }

  const sendState = documentSendState(quote)
  const totals =
    isProposalQuote(quote) || acceptNeedsChoice(quote)
      ? proposalRailTotals(quote)
      : { rows: quoteTotals(quote, partsOf(quote.lines, quote)), total: ['Total', quote.totalIncTax] as [string, number] }

  return (
    <BillingRail>
      <Totals rows={totals.rows} total={totals.total} />
      <SendState state={sendState} kind="quote" />
      <ViewedNote quote={quote} />
      <QuoteActions
        quote={quote}
        onSend={() => setSending(true)}
        onMarkSent={() => setMarking(true)}
        revise={revise}
        respond={respond}
        sendState={sendState}
        stacked
      />
      <SendDocumentPopup
        kind="quote"
        id={quote.id}
        number={quote.number}
        addressee={addressee}
        open={sending}
        onClose={() => setSending(false)}
      />
      <MarkSentPopup
        kind="quote"
        id={quote.id}
        number={quote.number}
        open={marking}
        onClose={() => setMarking(false)}
      />
    </BillingRail>
  )
}

function InvoiceBlock({
  invoiceId,
  railTakesTotals,
  yieldToProposal,
  addressee,
}: {
  invoiceId: string
  railTakesTotals: boolean
  /** On a phone the proposal sits above this document, so issuing steps back. */
  yieldToProposal?: boolean
  addressee: SendAddressee
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { can } = useSession()
  const [paying, setPaying] = useState(false)
  const [sending, setSending] = useState(false)
  const [marking, setMarking] = useState(false)

  const {
    data: invoice,
    isPending,
    error,
    refetch,
  } = useQuery(trpc.invoices.byId.queryOptions({ id: invoiceId }))

  const invalidate = () => {
    void queryClient.invalidateQueries(trpc.invoices.pathFilter())
    void queryClient.invalidateQueries(trpc.jobs.pathFilter())
  }

  const issue = useMutation(trpc.invoices.issue.mutationOptions({ onSuccess: invalidate }))
  const updateLines = useMutation(trpc.invoices.update.mutationOptions({ onSuccess: invalidate }))

  if (error) return <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
  if (isPending) return <SkeletonRows rows={3} />

  const parts = partsOf(invoice.lines, invoice)
  const owing = invoice.totalIncTax - invoice.amountPaid
  const sendState = documentSendState(invoice)
  const actions = railTakesTotals ? null : (
    <InvoiceActions
      invoice={invoice}
      issue={issue}
      onRecord={() => setPaying(true)}
      onEmail={() => setSending(true)}
      onMarkSent={() => setMarking(true)}
      sendState={sendState}
      yieldsPrimary={yieldToProposal}
    />
  )

  return (
    <>
      <Panel padded={false}>
        <DocumentHeader
          label="Invoice"
          number={invoice.number}
          title={invoice.job?.title ?? null}
          tone={INVOICE_STATUS_TONE[invoice.status] ?? 'neutral'}
          status={humanise(invoice.status)}
          meta={
            invoice.issuedAt
              ? `Issued ${formatDate(invoice.issuedAt)}${invoice.dueAt ? ` · due ${formatDate(invoice.dueAt)}` : ''}`
              : 'Draft — not issued'
          }
        >
          {actions}
        </DocumentHeader>

        <LineTable
          lines={invoice.lines}
          clientId={invoice.client?.id ?? null}
          editable={can('INVOICE_EDIT') && invoiceLinesWritable(invoice)}
          onSave={(next) => updateLines.mutate({ id: invoice.id, lines: toLineInput(next) })}
          saving={updateLines.isPending}
          lockedReason={
            can('INVOICE_EDIT') && !invoiceLinesWritable(invoice)
              ? 'Lines cannot be changed once money has been received.'
              : null
          }
        />

        {railTakesTotals ? null : (
          <Totals
            rows={[
              ['Subtotal', parts.subtotal],
              [parts.taxLabel, parts.tax],
              ['Invoiced', invoice.totalIncTax],
              ['Paid', invoice.amountPaid],
            ]}
            total={['Owing', owing]}
          />
        )}

        {railTakesTotals ? null : (
          <div className="flex flex-col gap-1 border-t border-ink-100 px-4 py-3">
            <SendState state={sendState} kind="invoice" />
            <LatestSendTracking sends={invoice.sends} />
          </div>
        )}
      </Panel>

      <SendHistory sends={invoice.sends} />

      {railTakesTotals ? null : (
        <>
          <PaymentPopup
            invoiceId={invoice.id}
            number={invoice.number}
            owing={owing}
            open={paying}
            onClose={() => setPaying(false)}
          />
          <SendDocumentPopup
            kind="invoice"
            id={invoice.id}
            number={invoice.number}
            addressee={addressee}
            open={sending}
            onClose={() => setSending(false)}
          />
          <MarkSentPopup
            kind="invoice"
            id={invoice.id}
            number={invoice.number}
            open={marking}
            onClose={() => setMarking(false)}
          />
        </>
      )}

      <Panel>
        <PanelHeading
          title="Payments"
          hint={invoice.payments.length > 0 ? `${money(invoice.amountPaid)} received` : undefined}
        />
        {invoice.payments.length === 0 ? (
          <p className="text-[13px] text-ink-400">Nothing received against this invoice.</p>
        ) : (
          <ul className="flex flex-col divide-y divide-ink-100">
            {invoice.payments.map((payment) => (
              <li key={payment.id} className="flex items-center gap-2.5 py-2 text-[13px]">
                <span className="w-[88px] shrink-0 tabular-nums text-ink-600">
                  {formatDate(payment.receivedAt)}
                </span>
                <span className="truncate text-ink-700">{humanise(payment.method)}</span>
                <span className="ml-auto shrink-0 font-medium tabular-nums text-ink-900">
                  {money(payment.amount)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </>
  )
}

function InvoiceRail({
  invoiceId,
  addressee,
  proposal,
}: {
  invoiceId: string
  addressee: SendAddressee
  /** The Quote-stage lead, rendered above the invoice's own actions. */
  proposal?: ReactNode
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const [paying, setPaying] = useState(false)
  const [sending, setSending] = useState(false)
  const [marking, setMarking] = useState(false)

  const {
    data: invoice,
    error,
    refetch,
  } = useQuery(trpc.invoices.byId.queryOptions({ id: invoiceId }))

  const invalidate = () => {
    void queryClient.invalidateQueries(trpc.invoices.pathFilter())
    void queryClient.invalidateQueries(trpc.jobs.pathFilter())
  }

  const issue = useMutation(trpc.invoices.issue.mutationOptions({ onSuccess: invalidate }))

  // The proposal does not wait on the invoice. A slow or failed fetch of the
  // draft must not hide the action the stage is asking for, and the control
  // stays mounted so opening it is not thrown away when the figures arrive.
  const ready = invoice && !error ? invoice : null
  const parts = ready ? partsOf(ready.lines, ready) : null
  const owing = ready ? ready.totalIncTax - ready.amountPaid : 0
  const sendState = ready ? documentSendState(ready) : null

  return (
    <BillingRail>
      {error ? (
        <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
      ) : !ready || !parts ? (
        <SkeletonRows rows={3} />
      ) : (
        <>
          <Totals
            rows={[
              ['Subtotal', parts.subtotal],
              [parts.taxLabel, parts.tax],
              ['Invoiced', ready.totalIncTax],
              ['Paid', ready.amountPaid],
            ]}
            total={['Owing', owing]}
          />
          {sendState ? <SendState state={sendState} kind="invoice" /> : null}
          <LatestSendTracking sends={ready.sends} />
        </>
      )}
      {proposal}
      {ready && sendState ? (
        <>
          <InvoiceActions
            invoice={ready}
            issue={issue}
            onRecord={() => setPaying(true)}
            onEmail={() => setSending(true)}
            onMarkSent={() => setMarking(true)}
            sendState={sendState}
            yieldsPrimary={Boolean(proposal)}
            stacked
          />
          <PaymentPopup
            invoiceId={ready.id}
            number={ready.number}
            owing={owing}
            open={paying}
            onClose={() => setPaying(false)}
          />
          <SendDocumentPopup
            kind="invoice"
            id={ready.id}
            number={ready.number}
            addressee={addressee}
            open={sending}
            onClose={() => setSending(false)}
          />
          <MarkSentPopup
            kind="invoice"
            id={ready.id}
            number={ready.number}
            open={marking}
            onClose={() => setMarking(false)}
          />
        </>
      ) : null}
    </BillingRail>
  )
}

function BillingRail({ children }: { children: ReactNode }) {
  return (
    <aside
      aria-label="Billing"
      className="flex shrink-0 flex-col gap-3 border-t border-ink-200 bg-ink-50 px-5 py-4 lg:w-[288px] lg:overflow-y-auto lg:border-t-0"
    >
      {children}
    </aside>
  )
}

function QuoteActions({
  quote,
  onSend,
  onMarkSent,
  revise,
  respond,
  sendState,
  stacked,
}: {
  quote: RouterOutputs['quotes']['byId']
  /** Opens the send sheet. Sending is a confirmation step, not a click. */
  onSend: () => void
  /** Records a copy that went out on paper. */
  onMarkSent: () => void
  revise: { isPending: boolean; mutate: (input: { id: string }) => void }
  respond: { isPending: boolean; mutate: (input: { id: string; outcome: 'ACCEPTED' }) => void }
  sendState: DocumentSendState
  stacked?: boolean
}) {
  const { can } = useSession()
  const [accepting, setAccepting] = useState(false)
  const settled = quote.status === 'ACCEPTED' || quote.status === 'DECLINED'
  // Accepted is final and superseded is history: the server refuses to send
  // or revise either, so neither is offered.
  const closed = quote.status === 'ACCEPTED' || quote.status === 'SUPERSEDED'
  const size = stacked ? 'md' : 'sm'

  // A stale quote's most important action is sending the corrected one, so it
  // takes the primary slot from Accept. Accepting a price the customer has not
  // been shown is the mistake this is here to prevent.
  const sendIsUrgent = sendState.stale

  // With options or extras on it, a yes has to say which — that is a popup,
  // not a click.
  const accept = () =>
    acceptNeedsChoice(quote) ? setAccepting(true) : respond.mutate({ id: quote.id, outcome: 'ACCEPTED' })

  return (
    <div className={cn('flex flex-wrap gap-1.5', stacked && 'flex-col gap-2')}>
      {can('QUOTE_EDIT') && !settled && !closed && !sendIsUrgent ? (
        <Button
          size={size}
          variant="primary"
          block={stacked}
          loading={respond.isPending}
          onClick={accept}
        >
          {stacked ? 'Accept and start the work' : 'Mark accepted'}
        </Button>
      ) : null}
      <OfficeAcceptPopup quote={quote} open={accepting} onClose={() => setAccepting(false)} />
      {can('QUOTE_SEND') && !closed ? (
        <Button
          size={size}
          variant={sendIsUrgent ? 'primary' : 'secondary'}
          block={stacked}
          onClick={onSend}
        >
          {sendIsUrgent ? 'Send the new figures' : quote.sentAt ? 'Send again' : 'Send'}
        </Button>
      ) : null}
      <DocumentPdf
        kind="quote"
        id={quote.id}
        document={quote.document}
        documentHash={quote.documentHash}
        contentHash={quote.contentHash}
        size={size}
        block={stacked}
      />
      {can('QUOTE_SEND') && !closed ? (
        <Button size={size} variant="ghost" block={stacked} onClick={onMarkSent}>
          Sent another way
        </Button>
      ) : null}
      {can('QUOTE_EDIT') && !sendIsUrgent && !closed ? (
        <Button
          size={size}
          block={stacked}
          loading={revise.isPending}
          onClick={() => revise.mutate({ id: quote.id })}
        >
          {stacked ? 'Revise the price' : 'New version'}
        </Button>
      ) : null}
    </div>
  )
}

function InvoiceActions({
  invoice,
  issue,
  onRecord,
  onEmail,
  onMarkSent,
  sendState,
  yieldsPrimary,
  stacked,
}: {
  invoice: {
    id: string
    status: string
    sentAt: Date | string | null
    document?: { id: string; checksum: string | null; filename: string } | null
    documentHash?: string | null
    contentHash?: string | null
  }
  issue: { isPending: boolean; mutate: (input: { id: string }) => void }
  onRecord: () => void
  onEmail: () => void
  onMarkSent: () => void
  sendState: DocumentSendState
  /** A proposal is the lead, so issuing and taking payment are no longer the green button. */
  yieldsPrimary?: boolean
  stacked?: boolean
}) {
  const { can } = useSession()
  const size = stacked ? 'md' : 'sm'
  const lead = yieldsPrimary ? 'secondary' : 'primary'

  // A draft has a provisional number and a void invoice is not owed, so
  // neither is sendable. The server refuses both as well.
  const sendable = invoice.status !== 'DRAFT' && invoice.status !== 'VOID'

  // The button that used to say "Email again" about figures the customer had
  // never seen. This is the whole reason the send rows exist.
  const sendIsUrgent = sendState.stale

  return (
    <div className={cn('flex flex-wrap gap-1.5', stacked && 'flex-col gap-2')}>
      {/* Take payment is in the mockup and not here: no gateway is connected. */}
      {can('INVOICE_SEND') && sendable ? (
        <Button
          size={size}
          variant={sendIsUrgent ? 'primary' : 'secondary'}
          block={stacked}
          onClick={onEmail}
        >
          {sendIsUrgent
            ? 'Email the new figures'
            : invoice.sentAt
              ? 'Email again'
              : 'Email invoice'}
        </Button>
      ) : null}
      <DocumentPdf
        kind="invoice"
        id={invoice.id}
        document={invoice.document}
        documentHash={invoice.documentHash}
        contentHash={invoice.contentHash}
        size={size}
        block={stacked}
      />
      {can('INVOICE_SEND') && sendable ? (
        <Button size={size} variant="ghost" block={stacked} onClick={onMarkSent}>
          Sent another way
        </Button>
      ) : null}
      {can('INVOICE_ISSUE') && invoice.status === 'DRAFT' ? (
        <Button
          size={size}
          variant={lead}
          block={stacked}
          loading={issue.isPending}
          onClick={() => issue.mutate({ id: invoice.id })}
        >
          Issue invoice
        </Button>
      ) : null}
      {can('PAYMENT_RECORD') && invoice.status !== 'DRAFT' ? (
        <Button size={size} variant={lead} block={stacked} onClick={onRecord}>
          Record payment
        </Button>
      ) : null}
    </div>
  )
}

function DocumentHeader({
  label,
  number,
  title,
  tone,
  status,
  meta,
  children,
}: {
  label: string
  number: string
  title: string | null
  tone: 'neutral' | 'blue' | 'green' | 'amber' | 'red' | 'purple'
  status: string
  meta: string
  children: ReactNode
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-ink-100 px-4 py-3.5">
      <Label>{label}</Label>
      <span className="font-mono text-[13px] font-semibold text-ink-600">{number}</span>
      {title ? <span className="text-sm font-semibold text-ink-900">{title}</span> : null}
      <Badge tone={tone}>{status}</Badge>
      <span className="text-xs tabular-nums text-ink-500">{meta}</span>
      <div className="ml-auto flex flex-wrap gap-1.5">{children}</div>
    </div>
  )
}

/**
 * What the table reads, rather than either row type.
 *
 * Quote lines and invoice lines are separate Prisma models that happen to
 * agree on everything except `isOptional`, so neither is assignable to the
 * other and typing this as one of them would make the other a cast. The
 * quantity is a `Decimal`, which is only ever rendered.
 */
interface Line {
  id: string
  description: string
  quantity: { toString: () => string }
  unitPrice: number
  /** Kept when the price was typed inc GST; an edit to the unit price here clears it. */
  unitPriceIncTax?: number | null
  taxRateBasisPoints: number
  lineTotalExTax: number
  lineTax: number
  lineTotalIncTax: number
  material: { name: string; code?: string | null } | null
  taxRate: { name: string } | null
  taxRateId?: string | null
  unitCost?: number
  discountBasisPoints?: number
  isOptional?: boolean
  sortOrder?: number
  materialId?: string | null
}

const COLUMNS = 'grid grid-cols-[1fr_64px_96px_72px_104px] gap-3'
const EDIT_COLUMNS = 'grid grid-cols-[1fr_64px_96px_72px_104px_28px] gap-3'

/** The seed's GST 10% rate. Copied onto a new line so a free-text row is taxed. */
const GST = {
  taxRateId: 'taxrate_gst_default',
  taxRateBasisPoints: 1000,
  taxRate: { name: 'GST 10%' },
}

function invoiceLinesWritable(invoice: { status: string; amountPaid: number }): boolean {
  if (invoice.status === 'VOID' || invoice.status === 'WRITTEN_OFF') return false
  if (invoice.amountPaid > 0 || invoice.status === 'PAID' || invoice.status === 'PARTIALLY_PAID') {
    return false
  }
  return invoice.status === 'DRAFT' || invoice.status === 'ISSUED' || invoice.status === 'OVERDUE'
}

function LineTable({
  lines,
  clientId,
  editable = false,
  onSave,
  saving,
  lockedReason,
}: {
  lines: Line[]
  /** Prices a catalog pick at this client's tier. */
  clientId: string | null
  editable?: boolean
  onSave?: (lines: Line[]) => void
  saving?: boolean
  lockedReason?: string | null
}) {
  const { can } = useSession()
  const [drafts, setDrafts] = useState<Line[]>(lines)
  const [creating, setCreating] = useState<{ index: number; name: string } | null>(null)

  useEffect(() => {
    setDrafts(lines)
  }, [lines])

  const rows = editable ? drafts : lines
  const columns = editable ? EDIT_COLUMNS : COLUMNS

  const patch = (index: number, next: Partial<Line>) =>
    setDrafts((current) =>
      current.map((row, i) => (i === index ? previewLine({ ...row, ...next }) : row)),
    )

  // An item with no tax rate of its own keeps the line's, the way
  // `catalog.linesForJob` falls back to the default rate.
  const pick = (index: number, item: CatalogItem) =>
    patch(index, {
      description: item.name,
      materialId: item.id,
      material: { name: item.name, code: item.code },
      unitPrice: item.unitPrice,
      unitPriceIncTax: null,
      unitCost: item.unitCost,
      ...(item.taxRateId
        ? {
            taxRateId: item.taxRateId,
            taxRateBasisPoints: item.taxRateBasisPoints,
            taxRate: item.taxRateName ? { name: item.taxRateName } : null,
          }
        : {}),
    })

  if (!editable && lines.length === 0) {
    return <EmptyState title="No lines" description="Nothing has been priced on this document." />
  }

  return (
    <div>
      <div
        className={`${columns} border-b border-ink-200 bg-ink-100 px-4 py-2 text-[10px] font-bold tracking-[0.06em] text-ink-500 uppercase`}
      >
        <span>Item</span>
        <span className="text-right">Qty</span>
        <span className="text-right">Unit</span>
        <span className="text-right">GST</span>
        <span className="text-right">Amount</span>
        {editable ? <span className="sr-only">Remove</span> : null}
      </div>

      {rows.map((line, index) => (
        <div
          key={line.id}
          className={`${columns} ${editable ? 'items-start' : 'items-baseline'} border-b border-ink-100 px-4 py-2.5 text-[13px]`}
        >
          {editable ? (
            <>
              <div className="flex min-w-0 flex-col gap-0.5">
                <CatalogLineField
                  index={index}
                  value={line.description}
                  clientId={clientId}
                  searchable={can('CATALOG_VIEW')}
                  canCreate={can('CATALOG_EDIT')}
                  // Emptying the box is starting the line again, so it lets go of the item.
                  onChange={(description) =>
                    patch(index, description.trim() ? { description } : { description, materialId: null, material: null })
                  }
                  onPick={(item) => pick(index, item)}
                  onCreate={(name) => setCreating({ index, name })}
                />
                <LineItemMeta line={line} />
              </div>
              <input
                aria-label={`Line ${index + 1} quantity`}
                value={quantityOf(line)}
                onChange={(event) =>
                  patch(index, { quantity: { toString: () => event.target.value } })
                }
                className="border-0 bg-transparent text-right tabular-nums text-ink-700 outline-none"
              />
              <input
                aria-label={`Line ${index + 1} unit price`}
                inputMode="decimal"
                value={(line.unitPrice / 100).toString()}
                onChange={(event) => {
                  const cents = Math.round(Number(event.target.value) * 100)
                  if (!Number.isFinite(cents)) return
                  patch(index, { unitPrice: cents, unitPriceIncTax: null })
                }}
                className="border-0 bg-transparent text-right tabular-nums text-ink-700 outline-none"
              />
              <span className="text-right text-xs text-ink-500">
                {line.taxRateBasisPoints > 0 ? 'incl.' : '—'}
              </span>
              <span className="text-right font-medium tabular-nums text-ink-900">
                {money(line.lineTotalIncTax)}
              </span>
              <button
                type="button"
                aria-label={`Remove line ${index + 1}`}
                onClick={() => setDrafts((current) => current.filter((_, i) => i !== index))}
                className="flex size-7 items-center justify-center text-ink-400 hover:text-ink-800"
              >
                <Icon name="close" className="size-3.5" />
              </button>
            </>
          ) : (
            <>
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="truncate font-medium text-ink-900">{line.description}</span>
                <LineItemMeta line={line} />
              </span>
              <span className="text-right tabular-nums text-ink-700">{quantityOf(line)}</span>
              <span className="text-right tabular-nums text-ink-700">{money(line.unitPrice)}</span>
              <span className="text-right text-xs text-ink-500">
                {line.taxRateBasisPoints > 0 ? 'incl.' : '—'}
              </span>
              <span className="text-right font-medium tabular-nums text-ink-900">
                {money(line.lineTotalIncTax)}
              </span>
            </>
          )}
        </div>
      ))}

      {editable ? (
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-ink-100 px-4 py-2">
          <Button
            type="button"
            size="sm"
            onClick={() => setDrafts((current) => [...current, blankLine(current.length)])}
          >
            Add a line
          </Button>
          {onSave ? (
            <Button
              type="button"
              size="sm"
              variant="primary"
              loading={saving}
              onClick={() => onSave(drafts.filter((row) => row.description.trim()).map(previewLine))}
            >
              Save lines
            </Button>
          ) : null}
        </div>
      ) : null}

      {lockedReason ? (
        <p className="px-4 py-2.5 text-[12.5px] text-ink-500">{lockedReason}</p>
      ) : null}

      {editable ? (
        <CatalogItemPopup
          open={creating !== null}
          initialName={creating?.name ?? ''}
          onClose={() => setCreating(null)}
          onCreated={(item) => {
            if (creating) pick(creating.index, item)
          }}
        />
      ) : null}
    </div>
  )
}

/**
 * The catalog item under the line: its code, and its name when the line's
 * words have moved away from it.
 *
 * The code is the office's handle on the item and is never on the document —
 * the PDF, the invoice page and the email print the description only.
 */
function LineItemMeta({ line }: { line: Line }) {
  const code = line.material?.code
  const name = detailOf(line)
  if (!code && !name) return null

  return (
    <span className="flex min-w-0 items-baseline gap-1.5 text-xs text-ink-500">
      {code ? (
        <span className="shrink-0 font-mono text-[11px]" title="Item code. Not printed on the document.">
          <span className="sr-only">Item code </span>
          {code}
        </span>
      ) : null}
      {name ? <span className="truncate">{name}</span> : null}
    </span>
  )
}

function blankLine(sortOrder: number): Line {
  return previewLine({
    id: `new_${sortOrder}_${Date.now()}`,
    description: '',
    quantity: { toString: () => '1' },
    unitPrice: 0,
    taxRateBasisPoints: GST.taxRateBasisPoints,
    lineTotalExTax: 0,
    lineTax: 0,
    lineTotalIncTax: 0,
    material: null,
    taxRate: GST.taxRate,
    taxRateId: GST.taxRateId,
    unitCost: 0,
    discountBasisPoints: 0,
    sortOrder,
  })
}

/**
 * Live amount while somebody is typing. The server recalculates on save with
 * `money.calculateLine`; this is the same half-up arithmetic in integers so
 * a typed 2.5 × $9.50 does not flash a float on the way through.
 */
function previewLine(line: Line): Line {
  const quantity = Number(quantityOf(line))
  if (!Number.isFinite(quantity)) {
    return { ...line, lineTotalExTax: 0, lineTax: 0, lineTotalIncTax: 0 }
  }

  const scaled = Math.round(quantity * 10_000)

  if (line.unitPriceIncTax != null) {
    const inc = Math.round((scaled * line.unitPriceIncTax) / 10_000)
    const exOfInc = Math.round((inc * 10_000) / (10_000 + line.taxRateBasisPoints))
    return { ...line, lineTotalExTax: exOfInc, lineTax: inc - exOfInc, lineTotalIncTax: inc }
  }

  const ex = Math.round((scaled * line.unitPrice) / 10_000)
  const tax = Math.round((ex * line.taxRateBasisPoints) / 10_000)

  return { ...line, lineTotalExTax: ex, lineTax: tax, lineTotalIncTax: ex + tax }
}

function toLineInput(lines: Line[]) {
  return lines.map((line, index) => ({
    description: line.description,
    quantity: line.quantity.toString(),
    unitPrice: line.unitPrice,
    unitPriceIncTax: line.unitPriceIncTax ?? null,
    unitCost: line.unitCost ?? 0,
    discountBasisPoints: line.discountBasisPoints ?? 0,
    taxRateId: line.taxRateId ?? null,
    isOptional: line.isOptional ?? false,
    sortOrder: line.sortOrder ?? index,
    materialId: line.materialId ?? null,
  }))
}

/**
 * The second line under the item.
 *
 * The description is copied off the catalog item when the line is created and
 * then edited freely, so the two agree far more often than not. Repeating the
 * same words in smaller grey type is worse than leaving the row alone.
 */
function detailOf(line: Line): string | null {
  const name = line.material?.name
  return name && name !== line.description ? name : null
}

/** Quantity is a Decimal, and 4.000 is a 4 to everyone reading an invoice. */
function quantityOf(line: Line): string {
  const text = String(line.quantity)
  const value = Number(text)
  return Number.isFinite(value) ? String(value) : text
}

/**
 * Subtotal and tax, from the lines where there are lines.
 *
 * The ServiceM8 import wrote a document total and nothing else, so seven
 * years of invoices carry a real `totalIncTax` beside a `subtotalExTax` of
 * zero. Printing that zero states a figure nobody calculated, and next to a
 * four hundred dollar total it reads as a bug in the arithmetic rather than a
 * gap in the data. Null means "not known", and the panel draws a dash.
 */
function partsOf(
  lines: Line[],
  header: { subtotalExTax: number; totalTax: number; totalIncTax: number },
): { subtotal: number | null; tax: number | null; taxLabel: string } {
  const taxLabel = taxLabelFor(lines)

  if (lines.length > 0) {
    return {
      subtotal: lines.reduce((sum, line) => sum + line.lineTotalExTax, 0),
      tax: lines.reduce((sum, line) => sum + line.lineTax, 0),
      taxLabel,
    }
  }

  // A zero part under a non-zero total was never worked out. A document that
  // genuinely totals nothing has zero parts, and zero is the truth there.
  const known = (value: number) => (value === 0 && header.totalIncTax !== 0 ? null : value)

  return { subtotal: known(header.subtotalExTax), tax: known(header.totalTax), taxLabel }
}

/** "GST 10%" when the lines agree on one rate, which they almost always do. */
function taxLabelFor(lines: Line[]): string {
  const names = new Set(
    lines
      .filter((line) => line.taxRateBasisPoints > 0)
      .map((line) => line.taxRate?.name)
      .filter((name): name is string => Boolean(name)),
  )

  return names.size === 1 ? [...names][0]! : 'Tax'
}

function quoteTotals(
  quote: { discountAmount: number },
  parts: { subtotal: number | null; tax: number | null; taxLabel: string },
): Array<[string, number | null]> {
  const rows: Array<[string, number | null]> = [
    ['Subtotal', parts.subtotal],
    [parts.taxLabel, parts.tax],
  ]
  if (quote.discountAmount) rows.push(['Discount', -quote.discountAmount])
  return rows
}

function Totals({
  rows,
  total,
}: {
  rows: Array<[string, number | null]>
  total: [string, number]
}) {
  return (
    <dl className="flex flex-col gap-[7px] px-4 py-3.5">
      {rows.map(([label, value]) => (
        <div key={label} className="flex justify-between text-[12.5px]">
          <dt className="text-ink-500">{label}</dt>
          <dd className="tabular-nums text-ink-800">{value === null ? '—' : money(value)}</dd>
        </div>
      ))}
      <div className="flex items-baseline justify-between border-t border-ink-100 pt-[7px]">
        <dt className="text-[13.5px] font-bold text-ink-900">{total[0]}</dt>
        <dd className="text-[18px] font-bold tabular-nums text-ink-900">{money(total[1])}</dd>
      </div>
    </dl>
  )
}
